import { APP_BASE_HREF } from '@angular/common';
import { inject as ngInject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createSelector, select, Store } from '@ngrx/store';
import { combineLatest, pipe } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { appInject } from 'src/app/app-injector';
import { OWC_LOCAL_STORAGE_KEY } from 'src/app/core/config/web-storage.config';
import { LocalStorageService } from 'src/app/core/services/storage/local-storage/local-storage.service';
import { finishUpdateLocationStateAction } from 'src/app/features/configuration/store/configuration.actions';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';
import { WINDOW } from 'src/app/utils/window.token';

import { updateStage } from '../common/common.actions';
import { selectCurrentMotorization, selectIsInIntegratedMode } from '../common/common.selectors';

import { routerNavigationEndAction, storeLocationAndBaseHrefAction } from './router.actions';
import { selectIsMotorizationOrSummary, selectLocationWithMarketOrigin } from './router.selectors';

const selectVehicleIfOnMotorizationOrSummary = pipe(
  select(
    createSelector(selectIsMotorizationOrSummary, selectCurrentMotorization, (isMotorizationOrSummary, vehicle) => ({
      isMotorizationOrSummary,
      vehicle
    }))
  ),
  filter(({ isMotorizationOrSummary }) => isMotorizationOrSummary === true),
  map(({ vehicle }) => vehicle)
);

/**
 * If OWCC is integrated in OneWeb we update the 'callback-url'.
 * The 'callback-url' is required to jump back to the page where the user started the login from.
 */
export const updateLoginCallbackUrlOnRouteChange$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    combineLatest([
      ngInject(Store).select(selectIsInIntegratedMode),
      ngInject(Store).select(selectLocationWithMarketOrigin),
      // NOTE: when a vehicle ID gets updated there is no navigationEnd event.
      // reason is probably because we don't update via router.navigation but window.location. we probably should change this!?
      actions$.pipe(ofType(finishUpdateLocationStateAction))
    ]).pipe(
      map(([isIntegrated, location]) => ({ isIntegrated, location })),
      filterTruthy(),
      // once we update URLs via router (see above) we probably can use navigationEnd.urlAfterRedirects instead
      map(({ location }) => location?.href ?? ''),
      // we intentionally encode this as it gets decoded on read
      map(location => encodeURIComponent(location)),
      tap(encodedLocation => {
        appInject(LocalStorageService).set(OWC_LOCAL_STORAGE_KEY.LOGIN_CALLBACK_URL, encodedLocation);
      })
    ),
  { functional: true, dispatch: false }
);

export const retriggerStageUpdateOnMotorizationOrSummaryToChangeExteriorPerspective = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(routerNavigationEndAction),
      switchMap(() => appInject(Store).pipe(selectVehicleIfOnMotorizationOrSummary)),
      filterTruthy(),
      map(vehicle =>
        updateStage({
          stage: vehicle?.stageV2,
          enforceReset: true // to switch to exterior and rotate the car to the according position when clicking on motorization or summary
        })
      )
    ),
  { functional: true }
);

export const storeWindowLocationAndBaseHref = createEffect(
  (actions$ = ngInject(Actions), win = ngInject(WINDOW), baseHref = ngInject(APP_BASE_HREF)) =>
    actions$.pipe(
      ofType(routerNavigationEndAction),
      filterTruthy(),
      map(() => {
        // eslint-disable-next-line unicorn/prefer-structured-clone
        const location = JSON.parse(JSON.stringify(win.location)) as Location;
        return storeLocationAndBaseHrefAction({
          location: location,
          baseHref: baseHref
        });
      })
    ),
  { functional: true }
);
